import { Currency, LanguageLocale } from '../constants/common.constant';

/**
 * Rounds a number to a specified number of decimal places
 * Handles floating-point precision issues (e.g., 1.4699999999999989 → 1.5)
 * @param value - The number to round
 * @param decimalPlaces - Number of decimal places (defaults to 0 for whole numbers)
 * @returns Rounded number
 */
export const roundToDecimalPlaces = (
  value: number,
  decimalPlaces = 0
): number => {
  const multiplier = 10 ** decimalPlaces;
  return Math.round(value * multiplier) / multiplier;
};

/**
 * Formats a number with locale-aware thousands separators
 * @param value - The number to format
 * @param locale - The locale to use for formatting (defaults to 'en-US')
 * @param minimumFractionDigits - Minimum number of fraction digits (defaults to 0)
 * @param maximumFractionDigits - Maximum number of fraction digits (defaults to 5)
 * @returns Formatted number string
 */
const formatNumber = (
  value: number,
  locale = 'en-US',
  minimumFractionDigits = 0,
  maximumFractionDigits = 5
): string => {
  return new Intl.NumberFormat(locale, {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(value);
};

export { formatNumber };

/**
 * Formats currency with locale support
 *
 * @deprecated Use `<Money>` from `@brightdrop/localization-core`, or `useFormat().money`. Both read
 * the locale from the signed-in user rather than taking it as an argument, which is what stops the
 * default `en-US` here from quietly deciding how a French reader sees a price.
 *
 * @param value - The numeric amount to format. Must be a finite number.
 * @param locale - The locale used for formatting (default: EN_US).
 * @param currency - The currency code to format with (default: USD).
 * @param maximumFractionDigits - Number of fraction digits to display; also used as minimum (default: 2).
 * @returns The formatted currency string (e.g., "$1,234.56"), or an empty string when value is not finite.
 */
export const formatCurrency = (
  value: number,
  locale: string = LanguageLocale.EN_US,
  currency = Currency.USD,
  maximumFractionDigits = 2
): string => {
  if (!Number.isFinite(value)) {
    return '';
  }

  return value.toLocaleString(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits,
  });
};
